import { useId } from "react";

import { Accordion, AccordionItem } from "../../../../core/Accordion.js";
import { AppLink } from "../../../../foundations/AppLink.js";
import { cn } from "../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../foundations/interaction.js";
import { Section } from "../../../../patterns/Section.js";
import { SectionHeader } from "../../../../patterns/SectionHeader.js";
import type { RichText } from "../../../shared/content.js";
import type { FaqContent } from "../../homeContent.js";

/** Prose with its inline links, each a link in the accent text color. */
export function RichTextView({ text }: { text: RichText }) {
  return text.map((part, i) =>
    typeof part === "string" ? (
      part
    ) : (
      <AppLink
        // biome-ignore lint/suspicious/noArrayIndexKey: parts are fixed prose, never reordered.
        key={i}
        href={part.link.href}
        className={cn(
          "rounded-xs text-accent-text underline underline-offset-2 hover:text-text-primary",
          FOCUS_RING,
        )}
      >
        {part.text}
      </AppLink>
    ),
  );
}

/**
 * The FAQ: every question, each answer in prose with its inline links. The `split` variant sets the heading
 * beside the questions, `centered` both in a narrow column, `stacked` the heading over them.
 */
export function HomeFaq({
  variant = "split",
  ...faq
}: FaqContent & { variant?: "split" | "centered" | "stacked" }) {
  const id = useId();
  const centered = variant === "centered";
  return (
    <Section
      aria-labelledby={id}
      className={cn(
        "grid gap-8",
        variant === "split" && "md:grid-cols-[1fr_2fr]",
        centered && "max-w-3xl justify-items-stretch",
        variant === "stacked" && "gap-5",
      )}
    >
      <SectionHeader
        title={faq.title}
        titleId={id}
        subtitle={faq.subtitle}
        size={variant === "stacked" ? "sm" : "lg"}
        className={cn(centered && "items-center text-center")}
      />
      <Accordion>
        {faq.entries.map((entry) => (
          <AccordionItem
            key={entry.question}
            value={entry.question}
            question={entry.question}
            answer={<RichTextView text={entry.answer} />}
          />
        ))}
      </Accordion>
    </Section>
  );
}
